import type { LayerKey } from '@festina-life/api'
import { Button } from '@festina-life/ui/actions'
import { Alert } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { Dropdown, SimpleModal } from '@festina-life/ui/structure'
import { type ReactNode, useId, useState } from 'react'
import { useNavigate } from 'react-router'

import { configurationItem } from '../../system/paths'
import { useNavigationParams } from '../../system/useNavigationParams'
import type { ConfigurationItem } from '../../types/common'
import { asFailedSubmit } from '../draftCreationModal/draftCreationStatusReporter'
import type { DraftSubmitStatus } from '../draftCreationModal/types'
import { DraftLocationSelector } from '../draftLocationSelectors/DraftLocationSelector'

/** Everything an upload form needs to add a version at the picked location. */
type UploadVersionFormProps = {
  /** Form the modal's accepting control belongs to, so it can live outside of the form. */
  formId: string
  layer: LayerKey
  block: string
  onStatusChanged: (status: DraftSubmitStatus) => void
}

type UploadVersionFormat = {
  label?: string
  renderForm: (formProps: UploadVersionFormProps) => ReactNode
}

type Props = {
  itemId: string
  type: ConfigurationItem
  formatsConfiguration: [UploadVersionFormat, ...UploadVersionFormat[]]
}

export const UploadVersionButton = ({
  itemId,
  type,
  formatsConfiguration,
}: Props) => {
  const translate = useTranslate()
  const navigate = useNavigate()
  const formId = useId()
  const { layer, block } = useNavigationParams()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<DraftSubmitStatus>()
  const [selectedLayer, setSelectedLayer] = useState<LayerKey>(layer)
  const [selectedBlock, setSelectedBlock] = useState<string>(block)
  const [selectedFormatIndex, setSelectedFormatIndex] = useState(0)

  const handleClose = () => {
    setSubmitStatus(undefined)
    setIsModalOpen(false)
  }

  const handleOpen = (formatIndex: number) => {
    setSubmitStatus(undefined)
    setSelectedLayer(layer)
    setSelectedBlock(block)
    setSelectedFormatIndex(formatIndex)
    setIsModalOpen(true)
  }

  const handleStatusChanged = async (response: DraftSubmitStatus) => {
    setSubmitStatus(response)

    if (response.status !== 'success') {
      return
    }

    try {
      await navigate(
        configurationItem({
          workspaceName: response.addedToWorkspaceKey.name,
          server: response.addedToWorkspaceKey.branchKey.server,
          branch: response.addedToWorkspaceKey.branchKey.name,
          layer: selectedLayer,
          block: selectedBlock,
          itemId: response.createdDraft.name,
          type,
        }),
      )
    } catch (error) {
      // Keep the upload open so the user can see why the version is not in view.
      setSubmitStatus(asFailedSubmit(error))

      return
    }

    handleClose()
  }

  return (
    <>
      <SimpleModal
        isOpen={isModalOpen}
        title={translate('uploadVersionButton.modal.title', { itemId })}
        closeConfiguration={{ onClose: handleClose }}
        acceptConfiguration={{
          formId,
          loading: submitStatus?.status === 'submitting',
        }}
        bodyOuterClassName="max-w-xl"
      >
        {submitStatus?.status === 'error' && (
          <Alert
            severity="error"
            title={translate('uploadVersionButton.modal.error.title')}
            onClose={() => setSubmitStatus(undefined)}
          >
            {submitStatus.error.message}
          </Alert>
        )}
        <DraftLocationSelector
          layer={selectedLayer}
          block={selectedBlock}
          onLayerChange={setSelectedLayer}
          onBlockChange={setSelectedBlock}
        />
        {formatsConfiguration[selectedFormatIndex].renderForm({
          formId,
          layer: selectedLayer,
          block: selectedBlock,
          onStatusChanged: handleStatusChanged,
        })}
      </SimpleModal>
      {formatsConfiguration.length === 1 ? (
        <Button startIcon="ArrowUpTray" onClick={() => handleOpen(0)}>
          {translate('uploadVersionButton.action')}
        </Button>
      ) : (
        <Dropdown
          element={
            <Button startIcon="ArrowUpTray" endIcon="ChevronDown">
              {translate('uploadVersionButton.action')}
            </Button>
          }
          items={formatsConfiguration.map((format, formatIndex) => ({
            element: format.label,
            onClick: () => handleOpen(formatIndex),
          }))}
        />
      )}
    </>
  )
}
